import { httpAuth } from '../../config/apiClient'
import { IDummyPop, IPop, RootState } from 'constants/interfaces'
import { AnyAction } from 'redux'
import { ThunkAction } from 'redux-thunk'
import endpoints from 'constants/endpoints'
import { isNumber } from 'lodash'
import keys from 'constants/keys'
import { AiPopForm, AiRePopForm, PopFormValues, ServerPopData } from 'components/PopCreation/types'
import { PROMPT_TYPES } from './promts'
import { PROMPT_INJECTION_TYPE, POP_REFERENCE_TYPE } from 'constants/enums'

export const actionTypes = {
  SET_POPS: 'SET_POPS',
  SET_IS_LOADING: 'SET_IS_LOADING',
  SET_DUMMY_POP: 'SET_DUMMY_POP',
}

export const setPops = (pops: IPop[]) => ({
  type: actionTypes.SET_POPS,
  payload: pops,
})

export const setDummyPop = (pop: IDummyPop | null) => ({
  type: actionTypes.SET_DUMMY_POP,
  payload: pop,
})
export const setIsLoading = (loading: boolean) => ({
  type: actionTypes.SET_IS_LOADING,
  payload: loading,
})

const DEFAULT_NOTIFICATION_POP_TYPE = 1
const POP_UNAVAILABLE_TITLE = 'Content is no longer available'
const POP_UNAVAILABLE_BODY = 'This pop may have expired or already been acknowledged.'

interface IGetSinglePopOptions {
  showPlaceholder?: boolean
  title?: string | null
  body?: string | null
  popType?: number | null
}

const buildNotificationPop = ({
  popId,
  title,
  body,
  popType,
  isLoading,
  isUnavailable,
}: {
  popId: string
  title?: string | null
  body?: string | null
  popType?: number | null
  isLoading: boolean
  isUnavailable: boolean
}): IPop => {
  const now = Math.floor(Date.now() / 1000)

  return {
    pk: popId,
    sk: popId,
    created_at_ts: now,
    updated_at_ts: now,
    created_by: '',
    group: '',
    ack_btn_text: 'OK',
    pop_body: isUnavailable ? POP_UNAVAILABLE_BODY : body || '',
    pop_image_url: '',
    pop_title: isUnavailable ? POP_UNAVAILABLE_TITLE : title || '',
    pop_initiator: 0,
    recipient: '',
    status: 0,
    pop_icon_url: '',
    pop_type: popType || DEFAULT_NOTIFICATION_POP_TYPE,
    isNotificationPlaceholder: true,
    isNotificationLoading: isLoading,
    isNotificationUnavailable: isUnavailable,
  }
}

const getPopIdentity = (pop: IPop) => pop.pk ?? pop.sk

const upsertPop = (pops: IPop[], nextPop: IPop) => {
  const nextPopId = getPopIdentity(nextPop)
  const filteredPops = pops.filter((pop) => getPopIdentity(pop) !== nextPopId)

  return [nextPop, ...filteredPops]
}

const findExistingNotificationPop = (pops: IPop[], pop_id: string) =>
  pops.find((pop) => getPopIdentity(pop) === pop_id && !pop.isNotificationPlaceholder)

export const removePopLocal =
  (pop_id: string): ThunkAction<void, RootState, unknown, AnyAction> =>
  (dispatch, getState) => {
    const { pops } = getState().pops

    dispatch(setPops(pops.filter((pop) => getPopIdentity(pop) !== pop_id)))
  }

export const removeLinkPopLocal =
  (expectedPop: IPop): ThunkAction<boolean, RootState, unknown, AnyAction> =>
  (dispatch, getState) => {
    const { pops } = getState().pops
    const filteredPops = pops.filter(
      (pop) =>
        getPopIdentity(pop) !== getPopIdentity(expectedPop) ||
        pop.pop_type !== keys.POP_TYPE.LINK_POP ||
        pop.pop_body !== expectedPop.pop_body ||
        !!pop.isPreview !== !!expectedPop.isPreview
    )

    if (filteredPops.length === pops.length) return false

    dispatch(setPops(filteredPops))
    return true
  }

export const handleGetPops = (): ThunkAction<void, RootState, unknown, AnyAction> => async (dispatch, getState) => {
  const pop_config = getState().config.retailConfig?.pop_config
  if (pop_config?.quantity_limit && pop_config.time_limit)
    try {
      dispatch(setIsLoading(true))
      const params = {
        ...(isNumber(pop_config?.quantity_limit) ? { quantity_limit: pop_config?.quantity_limit } : {}),
        ...(isNumber(pop_config?.time_limit) ? { time_limit: pop_config?.time_limit } : {}),
        ...(isNumber(pop_config?.long_range_time_limit)
          ? { long_range_time_limit: pop_config?.long_range_time_limit }
          : {}),
      }
      const {
        data: { pops },
      } = await httpAuth.get(endpoints.pop, {
        params,
      })

      dispatch(setPops(pops))
    } catch (e) {
      dispatch(setIsLoading(false))
      console.log(e)
    } finally {
      dispatch(setIsLoading(false))
    }
}

export const handleGetSinglePop =
  (pop_id: string, options: IGetSinglePopOptions = {}): ThunkAction<Promise<void>, RootState, unknown, AnyAction> =>
  async (dispatch, getState) => {
    if (!pop_id) return

    const { pops: currentPops } = getState().pops
    const existingPop = options.showPlaceholder ? findExistingNotificationPop(currentPops, pop_id) : null

    if (existingPop) {
      dispatch(setPops(upsertPop(currentPops, existingPop)))
      return
    }

    if (options.showPlaceholder) {
      dispatch(
        setPops(
          upsertPop(
            currentPops,
            buildNotificationPop({
              popId: pop_id,
              title: options.title,
              body: options.body,
              popType: options.popType,
              isLoading: true,
              isUnavailable: false,
            })
          )
        )
      )
    }

    try {
      const { data } = await httpAuth.get(endpoints.popById, { params: { pop_id } })
      const hasPopWrapper = data && typeof data === 'object' && 'pop' in data
      const newPop: IPop | null = hasPopWrapper ? data.pop : data ?? null

      if (!newPop?.pop_type) {
        if (options.showPlaceholder) {
          const { pops } = getState().pops

          dispatch(
            setPops(
              upsertPop(
                pops,
                buildNotificationPop({
                  popId: pop_id,
                  title: options.title,
                  body: options.body,
                  popType: options.popType,
                  isLoading: false,
                  isUnavailable: true,
                })
              )
            )
          )
        }

        return
      }

      const { pops } = getState().pops
      const normalizedPop = { ...newPop, pk: newPop.pk ?? newPop.sk }

      dispatch(setPops(upsertPop(pops, normalizedPop)))
    } catch (error) {
      console.error('Failed to get pop:', error)

      if (options.showPlaceholder) {
        const { pops } = getState().pops

        dispatch(
          setPops(
            upsertPop(
              pops,
              buildNotificationPop({
                popId: pop_id,
                title: options.title,
                body: options.body,
                popType: options.popType,
                isLoading: false,
                isUnavailable: true,
              })
            )
          )
        )
      }
    }
  }

export const handleSubmitPop =
  ({
    pop_id,
    isRepop,
    pop_type,
  }: {
    pop_id: string
    isRepop: boolean
    pop_type: number
  }): ThunkAction<Promise<boolean>, RootState, unknown, AnyAction> =>
  async (dispatch, getState) => {
    if (pop_type === keys.POP_TYPE.LINK_POP) {
      try {
        await httpAuth.put(endpoints.submitPop, { pop_id, state: '1', pop_type })
        return true
      } catch (error) {
        console.error('Failed to acknowledge link pop:', error)
        return false
      }
    }

    const { pops } = getState().pops
    try {
      dispatch(setIsLoading(true))
      const filteredPops = pops.filter((pop) => pop.pk !== pop_id)
      dispatch(setPops(filteredPops))
      if (isRepop) {
        await httpAuth.put(endpoints.submitRePop, { pop_id })
      } else {
        await httpAuth.put(endpoints.submitPop, { pop_id, state: '1', pop_type })
      }
      return true
    } catch (e) {
      dispatch(setIsLoading(false))
      console.log(e)
      return false
    } finally {
      dispatch(setIsLoading(false))
    }
  }

const createNewPrompt = async (aiPayload: any): Promise<string> => {
  const { data } = await httpAuth.post(endpoints.prompt, aiPayload)
  return data.prompt_id
}

const updateExistingPrompt = async (body: any): Promise<void> => {
  const promptPayload = {
    prompt_id: body.prompt_id,
    prompt: body.pop_body || body.prompt,
    tags: body.tags,
    groups: body.groups,
    users: body.users,
  }
  await httpAuth.put(endpoints.prompt, { ...promptPayload })
}

const handleAiPopPrompts = async (body: any): Promise<void> => {
  const aiPayload = extractAiPopData(body as AiPopForm | AiRePopForm)

  let promptId = aiPayload.prompt_id || ''

  if (!promptId) {
    const payload = { ...aiPayload, data: { prompt_type: PROMPT_TYPES.CUSTOM } }

    const newPromptId = await createNewPrompt(payload)
    body.data.prompt_id = newPromptId
  } else {
    await updateExistingPrompt(aiPayload)
  }
}

export const editPop =
  ({
    body,
    isAiPop,
  }: {
    body: any
    isAiPop?: boolean
  }): ThunkAction<Promise<{ success: boolean; data?: any; prompt_id?: string }>, RootState, unknown, AnyAction> =>
  async (dispatch) => {
    try {
      dispatch(setIsLoading(true))

      if (isAiPop) {
        await handleAiPopPrompts(body)
        body.data.pop_body = ''
        delete body.data.pop_type
      }

      await httpAuth.put(endpoints.pop, { ...body })

      const resultPromptId = body.data.prompt_id
      return { success: true, data: body.pop_id, prompt_id: resultPromptId }
    } catch (e) {
      console.log(e)
      return { success: false, data: null }
    } finally {
      dispatch(setIsLoading(false))
    }
  }

//POPS V2
interface ICreatePop {
  body: PopFormValues
}

const extractAiPopData = (body: AiPopForm | AiRePopForm) => {
  const prompt = body.data.pop_body
  let groups: string[] = []
  let tags: string[] = []
  let users: string[] = []
  let prompt_id: string = body.data.prompt_id || ''
  //@ts-ignore
  const popType = body.pop_type || body.data.pop_type

  if (popType === keys.POP_TYPE.AI_POP) {
    const aiPopData = body as any
    groups = aiPopData.groups || aiPopData.data.groups || []
    tags = aiPopData.tags || aiPopData.data.tags || []
    users = aiPopData.users || aiPopData.data.users || []
  } else if (popType === keys.POP_TYPE.AI_RE_POP) {
    const aiRePopData = body as AiRePopForm
    groups = aiRePopData.data.repop_groups || []
    tags = aiRePopData.data.repop_tags || []
    users = [...(aiRePopData.data.repop_users || []), ...(aiRePopData.users || [])]
  }

  return {
    prompt,
    groups,
    tags,
    users,
    prompt_id,
  }
}

export const isAiPopType = (popType: number): boolean => {
  return popType === keys.POP_TYPE.AI_POP || popType === keys.POP_TYPE.AI_RE_POP
}

export const createPopAdminV =
  ({ body }: ICreatePop): ThunkAction<Promise<{ success: boolean; data: any }>, RootState, unknown, AnyAction> =>
  async (dispatch) => {
    try {
      dispatch(setIsLoading(true))

      if (isAiPopType(body.pop_type)) {
        const aiPayload = extractAiPopData(body as AiPopForm | AiRePopForm)

        try {
          let promtId = aiPayload.prompt_id || ''

          if (!promtId) {
            const payload = { ...aiPayload, data: { prompt_type: PROMPT_TYPES.CUSTOM } }
            const { data } = await httpAuth.post(endpoints.prompt, payload)
            promtId = data.prompt_id
          }

          if (promtId) {
            body.data = {
              ...body.data,
              prompt_id: promtId,
              pop_body: '',
            }
          }
        } catch (promptError) {
          console.error('Failed to create AI prompt:', promptError)
        }
      }

      const { data } = await httpAuth.post(endpoints.pop, body)

      return { success: true, data: data.pop_id }
    } catch (error) {
      console.error('Failed to create pop:', error)
      return { success: false, data: null }
    } finally {
      dispatch(setIsLoading(false))
    }
  }

export const getPopData =
  ({ pop_id }: { pop_id: string }): ThunkAction<Promise<ServerPopData>, RootState, unknown, AnyAction> =>
  async () => {
    try {
      const { data } = await httpAuth.get(endpoints.popData, { params: { pop_id } })
      return data
    } catch (error) {
      console.error('Failed to get pop data:', error)
      return null
    }
  }

interface IAIBriefingPopParams {
  tid: string
  title: string
  groups: string[]
  users: string[]
  tags: string[]
  profileImageUrl: string | null
}

export const createAIBriefingPop =
  ({
    tid,
    title,
    groups,
    users,
    tags,
    profileImageUrl,
  }: IAIBriefingPopParams): ThunkAction<Promise<string | null>, RootState, unknown, AnyAction> =>
  async (_, getState) => {
    const { rid } = getState().config.config
    try {
      const body = {
        rid,
        is_long_range_pop: false,
        pop_type: keys.POP_TYPE.AI_POP,
        is_scheduled: true,
        groups,
        users,
        tags,
        data: {
          pop_title: title,
          profile_image_url: profileImageUrl,
          pop_body: '',
          ack_btn_text: '',
          pop_image_url: null,
          video: null,
          push_title: '',
          push_body: '',
          is_inj_reports: false,
          is_voice_mode: false,
          prompt_id: keys.AI_IMAGE_SUBTASK.CONST_BRIEFING_POP_PROMPT_ID,
          prompt_injections: [{ type: PROMPT_INJECTION_TYPE.TASK_SUMMARY, params: { tid } }],
        },
      }
      const { data } = await httpAuth.post(endpoints.pop, body)
      return data?.pop_id ?? null
    } catch (error) {
      console.error('Failed to create AI briefing pop:', error)
      return null
    }
  }

interface IAIActionPopParams {
  title: string
  groups: string[]
  users: string[]
  tags: string[]
}

export const createAIActionPop =
  ({
    title,
    groups,
    users,
    tags,
  }: IAIActionPopParams): ThunkAction<Promise<string | null>, RootState, unknown, AnyAction> =>
  async (_, getState) => {
    const { rid } = getState().config.config
    try {
      const body = {
        rid,
        is_long_range_pop: false,
        pop_type: keys.POP_TYPE.REFERENCE,
        is_scheduled: true,
        groups,
        users,
        tags,
        data: {
          pop_title: title,
          pop_body: '',
          ack_btn_text: '',
          pop_image_url: null,
          video: null,
          push_title: '',
          push_body: '',
          reference_type: POP_REFERENCE_TYPE.TASKS,
          reference_params: { view: keys.DEFAULT_TASK_VIEWS.AI_TASKS },
        },
      }
      const { data } = await httpAuth.post(endpoints.pop, body)
      return data?.pop_id ?? null
    } catch (error) {
      console.error('Failed to create AI action pop:', error)
      return null
    }
  }

interface IDriveActionPopParams {
  folderName: string
  folderId: string
  groups: string[]
  users: string[]
  tags: string[]
}

// Create a Library "new node" ActionPop (reference_type DRIVE_FOLDER, is_scheduled: false) so the folder's
// audience is notified and the CTA opens library/{folderId}. Fire-and-forget: never throws — returns
// pop_id on success, null on any failure (node creation must not be blocked by a pop error).
export const createDriveActionPopWithJob =
  ({
    folderName,
    folderId,
    groups,
    users,
    tags,
  }: IDriveActionPopParams): ThunkAction<Promise<string | null>, RootState, unknown, AnyAction> =>
  async (_, getState) => {
    const { rid, audienceScope } = getState().config.config
    // null scope field = don't filter; [] = filter to nothing (see skills/audience-scope).
    const inScope = (ids: string[], scopedIds: string[] | null) =>
      scopedIds === null ? ids : ids.filter((id) => scopedIds.includes(id))

    // Unlike tasks/other services, the drive pop should NOT go to the creator — they just made the node.
    // Also drop anyone outside the caller's org-tree scope — the pop API 403s on out-of-scope recipients.
    const recipientGroups = inScope(groups, audienceScope.groupIds)
    const recipientUsers = inScope(
      users.filter((u) => u !== getState().auth.uid),
      audienceScope.userIds
    )
    const recipientTags = inScope(tags, audienceScope.tagIds)
    // No in-scope audience → nothing to notify.
    if (!recipientGroups.length && !recipientUsers.length && !recipientTags.length) return null
    try {
      const body = {
        rid,
        is_long_range_pop: false,
        pop_type: keys.POP_TYPE.REFERENCE,
        is_scheduled: false,
        groups: recipientGroups,
        users: recipientUsers,
        tags: recipientTags,
        data: {
          // Title is a fixed locale key (new_content_pop_title) rendered by the ActionPop handler when
          // pop_title is empty; the folder name goes in pop_body instead, bold-appended after the
          // translated description (see driveFolderActionPopHandler's interpolateBodyIntoDescription).
          pop_title: '',
          pop_body: folderName,
          ack_btn_text: '',
          pop_image_url: null,
          video: null,
          push_title: '',
          push_body: '',
          reference_type: POP_REFERENCE_TYPE.DRIVE_FOLDER,
          reference_params: { folder_id: folderId },
        },
      }
      const { data } = await httpAuth.post(endpoints.pop, body)
      return data?.pop_id ?? null
    } catch (error) {
      console.error('Failed to create drive action pop:', error)
      return null
    }
  }
